<template>
  <div class="image" v-if="renderImage" :class="{'skeleton':skeleton && !loaded}">
    <picture :key="url">
      <template v-for="(breakpoint, index) in reversedBreakpoints">
        <template v-for="type in types">
          <source
            :key="`${breakpoint}-${type}`"
            :type="type"
            :srcset="!lazy && getSrcset(reversedWidths[index], type)"
            :data-srcset="lazy && getSrcset(reversedWidths[index], type)"
            :media="`(min-width: ${breakpoint}px)`"
          />
        </template>
      </template>
      <img
        :srcset="!lazy && getDefaultSrcset()"
        :data-srcset="lazy && getDefaultSrcset()"
        :src="!lazy && formatUrl(widths[0], 1)"
        :data-src="lazy && formatUrl(widths[0], 1)"
        :class="{ lazyload: lazy }"
        :alt="alt"
        @load="loaded=true"
      />
    </picture>
  </div>
</template>

<script>
import useLifecycleHook from '@/compositions/useLifecycleHook.js';

const WEBP_TYPE = 'image/webp';
const JPG_TYPE = '';

export default {
  props: {
    lazy: {
      default: true,
      type: Boolean,
    },
    url: {
      default: null,
      type: String,
    },
    widths: {
      type: Array,
      default: () => [320, 425, 768, 970, 1300, 1920],
    },
    breakpoints: {
      type: Array,
      default: () => [320, 425, 768, 970, 1300, 1920],
    },
    densities: {
      default: () => [1, 2],
      type: Array,
    },
    types: {
      default: () => [WEBP_TYPE, JPG_TYPE],
      type: Array,
    },
    alt: {
      default: '',
      type: String,
    },
    renderOnMounted: {
      type: Boolean,
      default: false,
    },
    skeleton: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      JPG_TYPE,
      WEBP_TYPE,
      loaded: false
    };
  },
  setup(props) {
    const { mounted } = useLifecycleHook();

    return {
      renderImage: props.renderOnMounted ? mounted : true,
    };
  },
  computed: {
    reversedWidths() {
      return [...this.widths].reverse();
    },

    reversedBreakpoints() {
      return [...this.breakpoints].reverse();
    },
  },
  methods: {
    adjustedWidths(density) {
      return this.widths.map((width) => Math.round(width * density));
    },

    adjustedBreakpoints(density) {
      return this.breakpoints.map((breakpoint) => Math.round(breakpoint * density));
    },

    getQuality(width, density) {
      if (density > 1) {
        if (width <= 767) {
          return 40;
        }
        return 60;
      }
      return 80;
    },

    formatUrl(width, density, type) {
      const isWebp = type === WEBP_TYPE;
      const quality = this.getQuality(width, density);
      return this.url ? `${this.url.replace('{width}', Math.ceil(width * density)).replace('{q}', quality)}${isWebp ? '&fm=webp' : ''}` : null;
    },

    getSrcset(width, type) {
      return this.densities.map((density) => `${this.formatUrl(width, density, type)} ${density}x`).join(', ');
    },

    getDefaultSrcset() {
      return `${this.formatUrl(this.widths[0], 1)} 1x, ${this.formatUrl(this.widths[0], 2)} 2x`;
    },
  },
};
</script>

<style lang="scss" scoped>
.image {
  .lazyload,
  .lazyloading {
    opacity: 0;
  }
  .lazyloaded {
    opacity: 1;
    transition: opacity 100ms;
  }

  img {
    width: 100%;
  }
}
</style>
